import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { PreflightCopy } from '@signalvote/shared';
import { useFinanceHealth } from '@/hooks/queries/useFinanceDeadlines';
import { usePortalUploadModel } from '@/hooks/finance/usePortalUploadModel';
import { withReceivingOffice } from '../reports/receivingOffice';
import { formatDate } from '../utils';

/**
 * The receipt chase on the close hub. Report detail already carries the open item (ReceiptChaseOpenItem)
 * for a report confirmed filed without the receiving office's receipt, but treasurers work here. Each row
 * links to the report, where the receipt is recorded or the chase dismissed. Self-hides when nothing is
 * waiting, so it is safe in the always-visible band.
 */
export function ReceiptChaseAlerts() {
  const health = useFinanceHealth();
  const { portalName } = usePortalUploadModel();
  // An additive alert: until health answers it stays out, and the masthead owns the failure.
  if (health.status !== 'ready') return null;
  const reports = health.data.reportsAwaitingReceipt;
  if (reports.length === 0) return null;

  return (
    <div data-testid="receipt-chase-alerts" className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-amber-900">
            {withReceivingOffice(PreflightCopy.ReceiptChaseTitle, portalName)}
          </p>
          <p className="mt-0.5 text-[12.5px] leading-snug text-amber-800">
            That confirmation is your proof the report arrived on time. Open the report to record it.
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {reports.map((r) => (
              <li
                key={r.reportId}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 rounded-md border border-amber-200 bg-white px-3 py-2"
              >
                <Link
                  to="/admin/finance/reports/$reportId"
                  params={{ reportId: r.reportId }}
                  data-testid={`receipt-chase-link-${r.reportId}`}
                  className="text-[13px] font-medium text-amber-700 underline hover:text-amber-800"
                >
                  {r.reportName}
                </Link>
                {r.filedDate && (
                  <span className="text-[12px] text-slate-500">filed {formatDate(r.filedDate)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
